import React, { useEffect, useState } from 'react';
import styles from '../Upload.module.scss';
import { getAssetTypeName } from '../../../data/assets/helpers';
import { selectTemplatesAndCombinedOntology } from '../../../data/templateSlice';
import { useAppSelector } from '../../../app/hooks';
import { CreateFileSetAssets, UploadFile, getTable, promptModifyFileRow } from '../../../components/files/helpers';
import CSVUploader from './CsvFileUploader';

interface FileSetOverviewProps {
  selectedTemplate: any;
  fileSet: UploadFile[];
  fileSetAssets: any;
  setFileSetAssets: (newValue: any) => void;
  validationCheck: boolean;
}

export function FileSetOverview(props: FileSetOverviewProps) {
  const { selectedTemplate, fileSet, fileSetAssets, setFileSetAssets, validationCheck } = props;
  const { ontology } = useAppSelector(selectTemplatesAndCombinedOntology);
  const fileSetTypeName = getAssetTypeName(ontology.assetTypes[selectedTemplate.fileSetAssetType]);
  const [dataTable, setDataTable] = useState<{ headers: any[]; rows: any[] }>({ headers: [], rows: [] });
  const [csvData, setCsvData] = useState([]);

  const onRowClick = (pairKey: any) => {
    promptModifyFileRow(fileSetTypeName, fileSetAssets[pairKey], selectedTemplate).then((metadata) => {
      if (metadata) {
        setFileSetAssets((prevAssets) => ({
          ...prevAssets,
          [pairKey]: { ...prevAssets[pairKey], ...metadata },
        }));
      }
    });
  };

  useEffect(() => {
    if (csvData.length > 0) {
      const rowIdentifier = selectedTemplate.FileSetInputFile.identifier;
      for (const row of csvData) {
        if (fileSetAssets[row[rowIdentifier]]) {
          const updateFileAsset = Object.entries(fileSetAssets[row[rowIdentifier]]).map(([key, value]) => {
            if (row[key]) {
              return [key, row[key]];
            }
            return [key, value];
          });
          setFileSetAssets((prevAssets) => ({
            ...prevAssets,
            [row[rowIdentifier]]: Object.fromEntries(updateFileAsset),
          }));
        }
      }
    }
  }, [csvData]);

  useEffect(() => {
    if (Object.keys(fileSetAssets).length === 0 && fileSet.length > 0) {
      const tmpFileAssets = CreateFileSetAssets(selectedTemplate, fileSet);
      for (const key in tmpFileAssets) {
        for (const element of selectedTemplate.fileSetMetadata) {
          if (element.options && element.options.length >= 1) {
            tmpFileAssets[key][element.id] = element.options[0];
          }
        }
      }
      setFileSetAssets(tmpFileAssets);
    }
  }, []);

  useEffect(() => {
    const tmpTable = getTable(fileSetAssets, selectedTemplate, setFileSetAssets, onRowClick, validationCheck);
    if (tmpTable) {
      setDataTable(tmpTable);
    }
  }, [fileSetAssets, validationCheck, selectedTemplate]);

  const renderExampleTable = () => {
    const headers = selectedTemplate.FileSetInputFile.exampleTable.map((column: any) => (
      <th key={column.header}>{column.header}</th>
    ));
    const row = selectedTemplate.FileSetInputFile.exampleTable.map((column: any) => (
      <td key={column.header}>{column.value}</td>
    ));

    return (
      <>
        <div className={styles.exampleSampleSheet}>
          <h3 className={styles.exampleSampleSheetSubtitle}>Example of the sample sheet structure</h3>
          <table>
            <thead>
              <tr>{headers}</tr>
            </thead>
            <tbody>
              <tr>{row}</tr>
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <>
      {selectedTemplate.FileSetInputFile && (
        <>
          {selectedTemplate.FileSetInputFile.exampleTable && (
            <div className={styles.tableWrapper}>{renderExampleTable()}</div>
          )}
          <CSVUploader setCsvData={setCsvData} displayName={selectedTemplate.FileSetInputFile.displayName} />
        </>
      )}
      <div className={styles.tableWrapper}>
        {dataTable.headers.length > 0 && dataTable.rows.length > 0 && (
          <div className={styles.tableContainer}>
            <table>
              <thead>
                <tr>{dataTable.headers}</tr>
              </thead>
              <tbody>{dataTable.rows}</tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
